export interface IReturnFocus {
    selector: string;
    location?: Location;
}

const focusableSelector = [
    'a[href]',
    'area[href]',
    'input',
    'select',
    'textarea',
    'button',
    'iframe',
    '[tabindex]',
    '[contentEditable=true]'
].join(',');

/**
 * Returns a list of all focusable elements descendant from a particular ancestor.
 * @param {Element} [parent=document.body] The parent element within which to search for focusable elements.
 * @returns {Element[]} An array of all descendant elements of parent that can receive focus.
 */
export function getFocusableElements(parent: Element = document.body): HTMLElement[] {
    const focusable = Array.from<HTMLElement>(parent.querySelectorAll(focusableSelector));
    return focusable.slice().sort((a, b) => {
        const aTabindex = Math.max(Number(a.getAttribute('tabindex')), 0) || 0;
        const bTabindex = Math.max(Number(b.getAttribute('tabindex')), 0) || 0;
        // if they are the same, then return the dom order
        if(aTabindex === bTabindex) {
            return focusable.indexOf(a) < focusable.indexOf(b) ? -1 : 1;
        }
        // if a > 0 and b === 0, a comes first
        if(bTabindex === 0) {
            return -1;
        }
        // if b > 0 and a === 0, b comes first
        if(aTabindex === 0) {
            return 1;
        }
        // otherwise sort by tabindex
        return aTabindex < bTabindex ? -1 : 1;
    });
}

/**
 * Tests if an element is currently able to receive focus.
 * @param {Element} element The element to test.
 * @returns {boolean} True if the element is currently able to receive focus.
 */
export function canReceiveFocus(element: Element) {
    const { offsetHeight, offsetWidth } = (element as HTMLElement);
    const { display, visibility } = getComputedStyle(element);
    // ensure that explicitly non-focusable, disabled, or hidden elements cannot receive focus (this includes elements hidden in collapsed sections)
    // exception must be made for ess-toggle-elements (ess-switch, ess-checkbox, etc) as the input is technically hidden
    const canFocus = element.matches(':not([tabindex="-1"])') && element.matches(':not([disabled])') && element.matches(':not(router-view)')
        && display !== "none" && visibility !== "hidden" && (offsetHeight + offsetWidth > 0 || element.matches(".ess-toggle-element"));
    return canFocus;
}

/**
 * Focuses the first focusable element descendant from a particular ancestor.
 * @param {Element} [parent=document.body] The parent element within which to search for focusable elements.
 * @param {string} [selector] A filter on which focusable elements to include when searching.
 */
export function focusFirst(parent: Element = document.body, selector?: string) {
    const first = getFocusableElements(parent)
        .find(element => canReceiveFocus(element) && (!selector || element.matches(selector)))

    first?.focus();
}

/**
 * Focuses the last focusable element descendant from a particular ancestor.
 * @param {Element} [parent=document.body] The parent element within which to search for focusable elements.
 * @param {string} [selector] A filter on which focusable elements to include when searching.
 */
export function focusLast(parent: Element = document.body, selector?: string) {
    const last = getFocusableElements(parent)
        .reverse()
        .find(element => canReceiveFocus(element) && (!selector || element.matches(selector)))
    last?.focus();
}

/**
 * Programatically "tabs" to the previous focusable element.
 * @param {Element} [focusedElement=document.activeElement] The reference element from which to search for the previous focusable element.
 * @param {Element} [container=document.body] The ancestor element used to find previous focusable elements.
 * @param {string} [selector] A filter on which focusable elements to include when searching.
 */
export function focusPrevious(focusedElement: Element | null = document.activeElement, container: Element = document.body, selector?: string) {
    if(focusedElement === null) {
        focusLast();
    }
    else {
        const focusableElements = getFocusableElements(container as HTMLElement);
        const focusedIndex = focusableElements.indexOf(focusedElement as HTMLElement);
        const previous = [...focusableElements.slice(focusedIndex), ...focusableElements.slice(0, focusedIndex)]
            .reverse()
            .find((element) => canReceiveFocus(element) && (!selector || element.matches(selector)));
        previous?.focus();
    }
}

/**
 * Programatically "tabs" to the next focusable element.
 * @param {Element} [focusedElement=document.activeElement] The reference element from which to search for the next focusable element.
 * @param {Element} [container=document.body] The ancestor element used to find next focusable elements.
 * @param {string} [selector] A filter on which focusable elements to include when searching.
 */
export function focusNext(focusedElement: Element | null = document.activeElement, container: Element | null = document.body, selector?: string) {
    if(focusedElement === null) {
        focusFirst();
    }
    else {
        const focusableElements = getFocusableElements(container as HTMLElement);
        const focusedIndex = focusableElements.indexOf(focusedElement as HTMLElement);
        const next = [...focusableElements.slice(focusedIndex + 1), ...focusableElements.slice(0, focusedIndex + 1)]
            .find((element) => canReceiveFocus(element) && (!selector || element.matches(selector)));
        next?.focus();
    }
}

function cssSelectorString(element: Element): string {
    let cssString: string = "";

    let currentElement: Element | null = element;
    if(currentElement.parentElement == null) {
        cssString = currentElement.tagName;

        return cssString;
    }

    let firstElement: boolean = true;
    do {
        const tagName: string = currentElement.tagName.toLowerCase();
        const elementIndex = Array.prototype.indexOf.call(currentElement.parentElement.children, currentElement) + 1;

        let tempString: string = `${tagName}:nth-child(${elementIndex})`;
        if(firstElement) {
            firstElement = false;
        } else {
            tempString = `${tempString} > `;
        }
        cssString = `${tempString}${cssString}`;

        currentElement = currentElement.parentElement;
    } while(currentElement.parentElement != null)

    return cssString;
}

export function getFocusSelector(preferredPriorFocus?: HTMLElement): IReturnFocus {
    const returnFocus: IReturnFocus = {
        selector: ""
    };

    const activeEle = preferredPriorFocus ?? document.activeElement as HTMLElement;
    if(!activeEle) {
        return returnFocus;
    }

    returnFocus.selector = cssSelectorString(activeEle);

    return returnFocus;
}

export function returnFocus(returnFocus: IReturnFocus): void {
    let element: HTMLElement | null = document.querySelector(returnFocus.selector);
    const selectorArray = returnFocus.selector.split(" > ");

    let focusableElements: HTMLElement[] = [];
    let canFocusElement: HTMLElement | undefined = undefined;

    // Note: [EN] If element can receive focus, no point in parsing selector string.
    if(!(element && canReceiveFocus(element))) {
        do {
            element = document.querySelector(selectorArray.join(' > '));

            if(element) {
                focusableElements = getFocusableElements(element);
                canFocusElement = focusableElements.find((element) => canReceiveFocus(element));
            }
            selectorArray.pop();
        } while(selectorArray.length != 0 && !canFocusElement)

        if(canFocusElement) element = canFocusElement;
    }

    if(element) element.focus();
}
